import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import '@/App.css';
import { I18nProvider } from './i18n';
import { AuthProvider } from './auth/AuthContext';
import AuthCallback from './auth/AuthCallback';

import HomePage from './pages/HomePage';
import OfertasPage from './pages/OfertasPage';
import OfertaDetailPage, { OfertaModal } from './pages/OfertaDetailPage';
import { ProfesionalesPage, EmpresasPage } from './pages/DirectoryPages';
import { ProfesionalDetailPage, EmpresaDetailPage } from './pages/DetailPages';
import { ContentListingPage, ContentDetailPage } from './pages/ContentPages';
import { LoginPage, RegisterPage, ResetPasswordPage } from './pages/AuthPages';
import MyProfilePage from './pages/MyProfilePage';
import ContactoPage from './pages/ContactoPage';
import PublicarOfertaPage from './pages/PublicarOfertaPage';
import OnboardingPage from './pages/OnboardingPage';
import {
  ForumIndexPage, ForumCategoryPage, ForumBrandPage,
  ForumThreadPage, ManualsPage, ToolsPage,
} from './pages/ForumPages';
import AuthGate from './components/AuthGate';
import { KiuChatbot } from './components/KiuChatbot';
import { PendingReviews } from './components/PendingReviews';
import { PresenceProvider } from './components/Presence';
import { ForumProvider } from './components/ForumContext';
import { MessagingProvider } from './components/MessagingContext';
import MessagesPage from './pages/MessagesPage';
import SavedContactsPage from './pages/SavedContactsPage';
import { StageMotionProvider } from './components/StageMotion';
import ColaboraPage from './pages/ColaboraPage';
import DonacionesPage from './pages/DonacionesPage';
import SobrePage from './pages/SobrePage';
import LegalPage from './pages/LegalPage';
import LegalAdminPage from './pages/LegalAdminPage';
import { MercadoPage, MercadoDetailPage, MercadoPublishPage } from './pages/MercadoPages';
import BibliotecaPage, { BibliotecaDocPage } from './pages/BibliotecaPage';
import AdminPage from './pages/AdminPage';
import { Toaster } from 'sonner';

const AppRouter = () => {
  const location = useLocation();
  // CRITICAL: detect OAuth callback synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  const backgroundLocation = location.state?.backgroundLocation;
  return <>
    <Routes location={backgroundLocation || location}>
      <Route path="/" element={<HomePage />} />
      <Route path="/ofertas" element={<OfertasPage />} />
      <Route path="/ofertas/:id" element={<OfertaDetailPage />} />
      <Route path="/profesionales" element={<AuthGate><ProfesionalesPage /></AuthGate>} />
      <Route path="/profesionales/:id" element={<AuthGate><ProfesionalDetailPage /></AuthGate>} />
      <Route path="/mi-perfil" element={<MyProfilePage />} />
      <Route path="/mensajes" element={<AuthGate><MessagesPage /></AuthGate>} />
      <Route path="/mensajes/:id" element={<AuthGate><MessagesPage /></AuthGate>} />
      <Route path="/contactos" element={<AuthGate><SavedContactsPage /></AuthGate>} />
      <Route path="/empresas" element={<AuthGate><EmpresasPage /></AuthGate>} />
      <Route path="/empresas/:id" element={<AuthGate><EmpresaDetailPage /></AuthGate>} />
      <Route path="/foro" element={<AuthGate><ForumIndexPage /></AuthGate>} />
      <Route path="/foro/manuales" element={<AuthGate><ManualsPage /></AuthGate>} />
      <Route path="/foro/herramientas" element={<AuthGate><ToolsPage /></AuthGate>} />
      <Route path="/foro/hilo/:id" element={<AuthGate><ForumThreadPage key={location.pathname} /></AuthGate>} />
      <Route path="/foro/:category" element={<AuthGate><ForumCategoryPage /></AuthGate>} />
      <Route path="/foro/:category/:brand" element={<AuthGate><ForumBrandPage /></AuthGate>} />
      <Route path="/onboarding" element={<OnboardingPage />} />
      <Route path="/actualidad" element={<ContentListingPage />} />
      <Route path="/actualidad/:id" element={<ContentDetailPage kind="news" />} />
      <Route path="/formacion" element={<Navigate to={`/actualidad?${new URLSearchParams({ ...Object.fromEntries(new URLSearchParams(location.search)), tipo: 'training' })}`} replace />} />
      <Route path="/formacion/:id" element={<AuthGate><ContentDetailPage kind="training" /></AuthGate>} />
      <Route path="/colabora" element={<ColaboraPage />} />
      <Route path="/donaciones" element={<DonacionesPage />} />
      <Route path="/apoyar" element={<Navigate to="/donaciones" replace />} />
      <Route path="/sobre" element={<SobrePage />} />
      <Route path="/legal" element={<LegalPage />} />
      <Route path="/legal-admin" element={<LegalAdminPage />} />
      <Route path="/biblioteca" element={<BibliotecaPage />} />
      <Route path="/biblioteca/:id" element={<BibliotecaDocPage />} />
      <Route path="/admin" element={<AdminPage />} />
      <Route path="/mercado" element={<MercadoPage />} />
      <Route path="/mercado/publicar" element={<MercadoPublishPage />} />
      <Route path="/mercado/:id" element={<MercadoDetailPage />} />
      <Route path="/eventos" element={<Navigate to={`/actualidad?${new URLSearchParams({ ...Object.fromEntries(new URLSearchParams(location.search)), tipo: 'events' })}`} replace />} />
      <Route path="/eventos/:id" element={<ContentDetailPage kind="events" />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/registro" element={<RegisterPage />} />
      <Route path="/restablecer" element={<ResetPasswordPage />} />
      <Route path="/contacto" element={<ContactoPage />} />
      <Route path="/publicar-oferta" element={<PublicarOfertaPage />} />
      <Route path="*" element={<HomePage />} />
    </Routes>
    {backgroundLocation && <Routes><Route path="/ofertas/:id" element={<OfertaModal />} /></Routes>}
  </>;
};

function App() {
  return (
    <div className="App">
      <I18nProvider>
        <BrowserRouter>
          <StageMotionProvider>
          <AuthProvider>
          <PresenceProvider>
          <MessagingProvider>
          <ForumProvider>
        <div className="route-screen" data-testid="route-screen">
            <AppRouter />
        </div>
            <KiuChatbot />
            <PendingReviews />
            <Toaster theme="dark" position="top-right" />
          </ForumProvider>
          </MessagingProvider>
          </PresenceProvider>
          </AuthProvider>
          </StageMotionProvider>
        </BrowserRouter>
      </I18nProvider>
    </div>
  );
}

export default App;
